import React, { useState } from 'react';
import { createReservation, hasReservationConflict } from '../../utils/reservationOperations';
import { useAuth } from '../../context/AuthContext';
import SimpleDateTimePicker from '../ui/SimpleDateTimePicker';

const ReservationForm = ({ itemId, itemName, onCreated }) => {
  const { currentUser, userDetails } = useAuth();
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (!start || !end) {
        setError('Please select start and end times');
        return;
      }
      const startDate = new Date(start);
      const endDate = new Date(end);

      setSubmitting(true);

      // Client-side conflict pre-check
      const conflict = await hasReservationConflict(itemId, startDate, endDate);
      if (conflict) {
        setError('This time conflicts with an existing reservation');
        setSubmitting(false);
        return;
      }

      const payload = await createReservation(itemId, {
        userId: currentUser?.uid || null,
        userName: userDetails?.name || currentUser?.displayName || null,
        pid: userDetails?.pid || null,
        start: startDate,
        end: endDate,
        notes: notes.trim()
      });

      setSuccess('Reservation created');
      setStart('');
      setEnd('');
      setNotes('');
      onCreated && onCreated(payload);
    } catch (err) {
      setError(err.message || 'Failed to create reservation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mb-8 p-4 bg-gray-50 rounded-lg">
      <h3 className="text-lg font-medium mb-3">Reserve This Item</h3>

      {error && (
        <div className="mb-3 p-2 bg-red-100 border-l-4 border-red-500 text-red-700 text-sm">{error}</div>
      )}
      {success && (
        <div className="mb-3 p-2 bg-green-100 border-l-4 border-green-500 text-green-700 text-sm">{success}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
            <SimpleDateTimePicker
              value={start}
              onChange={setStart}
              placeholder="Start time"
              minDate={new Date().toISOString()}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Until</label>
            <SimpleDateTimePicker
              value={end}
              onChange={setEnd}
              placeholder="End time"
              minDate={start || new Date().toISOString()}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any special notes for this reservation..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white text-black"
          />
        </div>

        <button
          type="submit"
          disabled={submitting || !start || !end}
          className="w-full py-3 px-4 bg-yellow-500 text-black font-medium rounded-md hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Creating...' : 'Reserve Item'}
        </button>
      </form>
    </div>
  );
};

export default ReservationForm;
